import React, { useMemo } from 'react';
import CustomModal from '../../../components/customModal/CustomModal';
import {
    displayInfo,
    EcwOrder,
    findMatchedOption,
    hasCompleteStatusKeyword,
    isValuePresent,
    RouteFieldOptions,
    RouteFieldOptionsThinj,
    SiteFieldOptions,
} from '../EcwConstants';

interface ThinjAdministrationDetailsDialogProps {
    show: boolean;
    onClose: () => void;
    order: EcwOrder | null | undefined;
}

const ThinjAdministrationDetailsDialog: React.FC<ThinjAdministrationDetailsDialogProps> = ({ show, onClose, order }) => {
    const presentFields = useMemo(() => {
        if (!order) return [];

        const medAdmin = order.medicationAdministration;

        // 1. Site
        const rawSite = medAdmin?.site;
        const matchedSite = findMatchedOption(rawSite, SiteFieldOptions);
        const siteDisplay = matchedSite?.value && matchedSite?.label
            ? `${matchedSite.label} (${matchedSite.value})`
            : rawSite ?? '';

        // 2. Route
        const rawRoute = medAdmin?.route;
        const matchedRoute = findMatchedOption(rawRoute, RouteFieldOptionsThinj);
        const routeDisplay = matchedRoute?.value && matchedRoute?.label
            ? `${matchedRoute.label} (${matchedRoute.value})`
            : rawRoute ?? '';

        // 3. Qty (dosage)
        const rawQty = medAdmin?.dosage;
        const qtyDisplay = rawQty != null ? String(rawQty) : '';

        // 4. Unit (dosage-unit)
        const rawUnit = medAdmin?.dosageUnit;
        const unitDisplay = rawUnit ?? '';

        // 5. NDC
        const rawNdc = medAdmin?.medicationCode;
        const ndcDisplay = rawNdc ?? '';

        // 6. Lot Number
        const rawLot = medAdmin?.lotNumber;
        const lotDisplay = rawLot ?? '';

        // 7. Status
        const rawStatus = order?.status;
        const statusDisplay = rawStatus ?? '';

        const givenFields = [
            { label: 'Site', value: siteDisplay, isPresent: isValuePresent(rawSite) },
            { label: 'Route', value: routeDisplay, isPresent: isValuePresent(rawRoute) },
            { label: 'Quantity (Dosage)', value: qtyDisplay, isPresent: isValuePresent(rawQty) },
            { label: 'Unit (Dosage Unit)', value: unitDisplay, isPresent: isValuePresent(rawUnit) },
            { label: 'NDC', value: ndcDisplay, isPresent: isValuePresent(rawNdc) },
            { label: 'Lot Number', value: lotDisplay, isPresent: isValuePresent(rawLot) },
            { label: 'Status', value: statusDisplay, isPresent: (isValuePresent(rawStatus) && hasCompleteStatusKeyword(rawStatus)) },
        ];

        return givenFields.filter(item => item.isPresent);
    }, [order]);

    if (!show) return null;

    return (
        <CustomModal
            id="ThinjAdministrationDetailsModal"
            title="eCW Administration Details"
            onClose={onClose}
            overlayClass="thinj-admin-details-overlay"
            content={
                <div className="thinj-admin-details-content">
                    {presentFields.length > 0 && (
                        <div className="thinj-admin-details-grid">
                            {presentFields.map((field) => (
                                <div key={field.label} className="detail-item">
                                    <span className="detail-label">{field.label}</span>
                                    <p className="detail-value">{displayInfo(field.value)}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            }
        />
    );
};

export default ThinjAdministrationDetailsDialog;
